import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

type EmployeeState =
  | { status: 'loading' }
  | { status: 'ready'; orgId: string }
  | { status: 'no_invite' }
  | { status: 'error'; message: string };

/**
 * Resolves the current user's pulse org membership.
 * If no membership exists yet (first sign-in), calls bootstrap-employee.
 */
export function useEmployee(): EmployeeState {
  const { user, isLoading: authLoading } = useAuth();
  const [state, setState] = useState<EmployeeState>({ status: 'loading' });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState({ status: 'loading' });
      return;
    }

    let cancelled = false;

    (async () => {
      // 1. Source of truth for employee membership is pulse_employees.
      //    pulse_org_members is reserved for admin/owner roles — admin-added
      //    employees have no row there, so checking it would force a
      //    bootstrap-employee round-trip on every load (and historically
      //    surfaced as "Charles not connecting" for new users).
      //    pulse_employees is structurally SELECT-denied, so resolve via
      //    SECURITY DEFINER RPC that only returns the caller's own org.
      const { data: orgId, error: rpcErr } = await supabase.rpc('my_pulse_org_id');

      if (cancelled) return;

      if (rpcErr) {
        setState({ status: 'error', message: rpcErr.message });
        return;
      }

      if (orgId) {
        setState({ status: 'ready', orgId: orgId as string });
        return;
      }

      // 2. No membership — call bootstrap-employee
      const { data, error } = await supabase.functions.invoke('bootstrap-employee', {
        body: {},
      });

      if (cancelled) return;

      // FunctionsHttpError leaves data=null and stuffs the Response on
      // error.context. Parse it so we can distinguish no_invite from
      // generic failures and surface a real message.
      let parsed: any = data;
      if (error && (error as any).context) {
        try {
          parsed = await (error as any).context.clone().json();
        } catch {
          try {
            parsed = { error: await (error as any).context.clone().text() };
          } catch {
            /* keep parsed = data */
          }
        }
      }

      if (error || parsed?.error) {
        if (parsed?.error === 'no_invite') {
          setState({ status: 'no_invite' });
        } else {
          // eslint-disable-next-line no-console
          console.error('[useEmployee] bootstrap-employee failed:', { error, parsed });
          const msg =
            parsed?.detail ??
            parsed?.message ??
            parsed?.error ??
            (error as any)?.message ??
            'bootstrap_failed';
          setState({ status: 'error', message: String(msg) });
        }
        return;
      }

      const bootstrappedOrgId = (parsed as any)?.employee?.org_id;
      if (!bootstrappedOrgId) {
        setState({ status: 'error', message: 'bootstrap returned no org_id' });
        return;
      }
      setState({ status: 'ready', orgId: bootstrappedOrgId });
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return state;
}

/** Convenience: returns orgId or null. */
export function useOrgId(): string | null {
  const s = useEmployee();
  return s.status === 'ready' ? s.orgId : null;
}
